import { useCallback, useMemo } from 'react'

import { useAtomValue, useSetAtom } from 'jotai'

import { presentationAtom } from 'Automations/automationEditor/atoms'
import {
  getStartNode,
  getUpstreamNodesWithOutputs,
  useResolvedNodeDefinitions
} from 'Automations/nodeDefinitions'
import { useNode } from 'Automations/nodeDefinitions/hooks'
import { ValidationResult } from 'Automations/nodeDefinitions/validation'
import { areAllConditionsExhausted } from '../../helpers'
import { useUpstreamConditionalProperties } from '../useUpstreamConditionalProperties'
import {
  autoOpenConditionIdAtom,
  selectedGroupIdAtom
} from '../../switch.atoms'
import { Condition, Config, SwitchGroup } from '../..'

export type SwitchEmptyStateReason =
  | 'NOT_CONNECTED'
  | 'START_NODE_TRIGGER_UNSET'
  | 'START_NODE_TRIGGER_NO_CONDITIONS'

type UseGroupEditing = (params: { nodeId: string }) => {
  group: SwitchGroup | undefined
  updateGroup: (params: Partial<Omit<SwitchGroup, 'id'>>) => void
  updateGroupLabel: (newLabel: string) => void
  conditions: Condition[]
  addCondition: () => void
  canAddCondition: boolean
  removeCondition: (conditionId: string) => void
  emptyStateReason: SwitchEmptyStateReason | undefined
  validationErrors: Record<string, ValidationResult[]>
}

export const useGroupEditing: UseGroupEditing = ({ nodeId }) => {
  const { nodeData, setNodeData, validationErrors } = useNode<Config>({
    nodeId
  })
  const selectedGroupId = useAtomValue(selectedGroupIdAtom)
  const setAutoOpenConditionId = useSetAtom(autoOpenConditionIdAtom)
  const presentation = useAtomValue(presentationAtom)
  const definitions = useResolvedNodeDefinitions()

  const group = useMemo(() => {
    return nodeData?.groups.find(({ id }) => selectedGroupId === id)
  }, [nodeData?.groups, selectedGroupId])

  const updateGroup = useCallback(
    (params: Partial<Omit<SwitchGroup, 'id'>>) => {
      if (!nodeData) return

      const updatedGroups = nodeData.groups.map(group => {
        if (group.id !== selectedGroupId) return group
        return { ...group, ...params }
      })

      setNodeData('groups', updatedGroups)
    },
    [nodeData, selectedGroupId, setNodeData]
  )

  const updateGroupLabel = useCallback(
    (newLabel: string) => updateGroup({ label: newLabel }),
    [updateGroup]
  )

  const conditions = useMemo(() => {
    if (!group) return []
    return group.conditions
  }, [group])

  const allProperties = useUpstreamConditionalProperties(nodeId, presentation)

  const canAddCondition = useMemo(() => {
    if (!allProperties) return true
    return !areAllConditionsExhausted({ conditions, allProperties })
  }, [conditions, allProperties])

  const addCondition = useCallback(() => {
    if (!group || !canAddCondition) return
    const newId = crypto.randomUUID()
    updateGroup({
      ...group,
      conditions: [
        ...group.conditions,
        { id: newId, property: '', operator: '', value: '' }
      ]
    })
    setAutoOpenConditionId(newId)
  }, [group, updateGroup, canAddCondition, setAutoOpenConditionId])

  const removeCondition = useCallback(
    (conditionId: string) => {
      if (!group) return
      updateGroup({
        ...group,
        conditions: group.conditions.filter(({ id }) => conditionId !== id)
      })
    },
    [group, updateGroup]
  )

  const emptyStateReason = useMemo((): SwitchEmptyStateReason | undefined => {
    if (allProperties !== undefined) return undefined
    if (!presentation) return 'NOT_CONNECTED'

    const upstreamNodes = getUpstreamNodesWithOutputs({
      nodes: presentation.nodes,
      edges: presentation.edges,
      targetNodeId: nodeId,
      definitions
    })

    const isConnectedToPlaceholderStart = upstreamNodes.some(
      ({ node }) => node.type === 'START_PLACEHOLDER'
    )
    if (isConnectedToPlaceholderStart) return 'START_NODE_TRIGGER_UNSET'

    const startNode = getStartNode(presentation, definitions)
    if (!startNode) return 'NOT_CONNECTED'

    const isConnectedToStart = upstreamNodes.some(
      ({ node }) => node.id === startNode.id
    )
    if (!isConnectedToStart) return 'NOT_CONNECTED'

    return 'START_NODE_TRIGGER_NO_CONDITIONS'
  }, [allProperties, presentation, nodeId, definitions])

  return {
    group,
    updateGroup,
    updateGroupLabel,
    conditions,
    addCondition,
    canAddCondition,
    removeCondition,
    emptyStateReason,
    validationErrors
  }
}
